import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// import { Plus, Edit2, Trash2, UserCheck, Mail, FileText, Send } from 'lucide-react';
import { Plus, Edit2, Trash2, UserCheck, Mail, FileText, Copy, ExternalLink, Send } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import ConfirmModal from '../components/common/ConfirmModal';
import AttendeeModal from '../components/attendees/AttendeeModal';
import EmailNotificationModal from '../components/EmailNotificationModal';
import AttendeeImportExport from '../components/attendees/AttendeeImportExport';
import EmailComposer from '../components/email/EmailComposer';
import Modal from '../components/common/Modal';
import { attendeeAPI, eventAPI, shortUrlAPI } from '../api/endpoints';
import { CHECK_IN_STATUS_LABELS } from '../utils/constants';
import { useToastContext } from '../context/ToastContext';
import { getAttendeeName, getAttendeeEmail, getAttendeePhone, getAttendeeCompany } from '../utils/attendeeHelpers';
import { getApiBaseUrl, logApiConfig } from '../utils/apiConfig';
import { useAuth } from '../context/AuthContext';
import { useEvent } from '../context/EventContext';
import EventSelector from '../components/common/EventSelector';
import { useNavigate } from 'react-router-dom';

const Attendees = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedEvent, setSelectedEvent } = useEvent();
  const [showAttendeeModal, setShowAttendeeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedAttendees, setSelectedAttendees] = useState([]);

  // Check if user can delete attendees (admin, super_admin, and event_admin can delete)
  const canDelete = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'event_admin';

  // Create short URL mutation
  const createShortUrlMutation = useMutation({
    mutationFn: (variables: { eventId: string; data: any }) => shortUrlAPI.create(variables.eventId, variables.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });

  // Check if user can access email settings (only admin and super_admin)
  const canManageEmailSettings = user?.role === 'admin' || user?.role === 'super_admin';

  // Test backend connectivity
  const { data: healthCheck } = useQuery({
    queryKey: ['health-check'],
    queryFn: async () => {
      try {
        logApiConfig();
        const baseUrl = getApiBaseUrl();
        const healthUrl = `${baseUrl.replace('/api', '')}/api/health`;
        console.log('🌡️ Testing backend health at:', healthUrl);

        const response = await fetch(healthUrl);
        const data = await response.json();
        console.log('🌡️ Health check response:', data);
        return data;
      } catch (error) {
        console.error('🌡️ Health check failed:', error);
        return null;
      }
    },
    staleTime: 30000, // Cache for 30 seconds
  });

  // Fetch events with attendee counts
  const { data: eventsData } = useQuery({
    queryKey: ['events-with-counts'],
    queryFn: async () => {
      console.log('🎆 Fetching events...');
      const response = await eventAPI.getAll({});
      console.log('🎆 Events response:', response);
      const events = response.data.events || [];
      return { events };
    },
  });

  // Fetch attendees (event-specific)
  const { data: attendeesData, isLoading } = useQuery({
    queryKey: ['attendees', selectedEvent, currentPage, itemsPerPage, searchTerm, statusFilter, genderFilter],
    queryFn: async () => {
      if (!selectedEvent) return { attendees: { data: [], total: 0, last_page: 1 } };

      const params = {
        paginate: true,
        page: currentPage,
        per_page: itemsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(genderFilter && { gender: genderFilter }),
      };

      try {
        const response = await attendeeAPI.getAll(selectedEvent, params);
        return response.data;
      } catch (error) {
        console.error('❌ Attendees API error:', error);
        console.error('❌ Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url
        });
        throw error;
      }
    },
    enabled: !!selectedEvent,
  });


  const attendeeMutation = useMutation({
    mutationFn: (variables: { mode: string; id?: any; data: any }) => {
      if (variables.mode === 'edit') {
        return attendeeAPI.update(selectedEvent, variables.id, variables.data);
      } else {
        return attendeeAPI.create(selectedEvent, variables.data);
      }
    },
    onSuccess: (response, variables) => {
      // Invalidate the correct attendees query key that includes selectedEvent
      queryClient.invalidateQueries({ queryKey: ['attendees', selectedEvent] });
      queryClient.invalidateQueries({ queryKey: ['attendees'] }); // Also invalidate general attendees queries
      queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
      setShowAttendeeModal(false);

      // Show success toast notification
      const attendeeData = response.data.attendee || response.data;
      const attendeeName = attendeeData.full_name || attendeeData.name || 'Attendee';

      if (variables.mode === 'edit') {
        toast.success(`${attendeeName} has been updated successfully!`);
      } else {
        toast.success(`${attendeeName} has been registered successfully!`);
      }

      // Reset form state
      setSelectedAttendee(null);
      setModalMode('add');
    },
    onError: (error: any, variables) => {
      console.error(`${variables.mode === 'edit' ? 'Update' : 'Create'} attendee error:`, error);

      // Enhanced error handling for 422 validation errors
      if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors || {};
        console.error('Validation errors:', validationErrors);

        // Show specific validation errors
        const errorMessages = Object.values(validationErrors).flat();
        if (errorMessages.length > 0) {
          toast.error(`Validation Error: ${errorMessages.join(', ')}`);
        } else {
          toast.error('Please check your form data and try again.');
        }
      } else {
        const errorMessage = variables.mode === 'edit'
          ? 'Failed to update attendee'
          : 'Failed to register attendee';
        toast.error(error.response?.data?.error || error.response?.data?.message || errorMessage);
      }
    },
  });

  // Delete attendee mutation
  const deleteMutation = useMutation({
    mutationFn: (id: any) => attendeeAPI.delete(selectedEvent, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendees', selectedEvent] });
      queryClient.invalidateQueries({ queryKey: ['attendees'] });
      queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
      setShowDeleteModal(false);
      setSelectedAttendee(null);
      toast.success('Attendee deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete attendee');
    },
  });

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationFn: (id: any) => attendeeAPI.checkIn(selectedEvent, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendees', selectedEvent] });
      queryClient.invalidateQueries({ queryKey: ['attendees'] });
      toast.success('Attendee checked in successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to check in attendee');
    },
  });

  // Bulk check-in mutation
  const bulkCheckInMutation = useMutation({
    mutationFn: (ids: any) => attendeeAPI.bulkCheckIn(selectedEvent, ids),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['attendees', selectedEvent] });
      queryClient.invalidateQueries({ queryKey: ['attendees'] });
      setSelectedAttendees([]);
      toast.success(response.data.message || 'Attendees checked in successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to check in attendees');
    },
  });

  // Unified handler for both add and edit
  const handleAttendeeSubmit = (...args) => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }

    if (modalMode === 'edit') {
      // Edit mode: args = [id, data]
      const [id, data] = args;
      attendeeMutation.mutate({ mode: 'edit', id, data });
    } else {
      // Add mode: args = [data]
      const [data] = args;
      attendeeMutation.mutate({ mode: 'add', data });
    }
  };

  const handleAddClick = () => {
    setModalMode('add');
    setSelectedAttendee(null);
    setShowAttendeeModal(true);
  };

  const handleEditClick = (attendee) => {
    setModalMode('edit');
    setSelectedAttendee(attendee);
    setShowAttendeeModal(true);
  };

  const handleDelete = () => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }

    if (selectedAttendee?.bulkDelete) {
      // Handle bulk deletion
      const attendeeIds = selectedAttendee.attendees.map(a => a.id);
      Promise.all(
        attendeeIds.map(id =>
          attendeeAPI.delete(selectedEvent, id)
        )
      ).then(() => {
        toast.success(`Successfully deleted ${selectedAttendee.count} attendees`);
        setSelectedAttendees([]); // Clear selection
        queryClient.invalidateQueries({ queryKey: ['attendees', selectedEvent] });
        queryClient.invalidateQueries({ queryKey: ['attendees'] });
        queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
        setShowDeleteModal(false);
        setSelectedAttendee(null);
      }).catch(error => {
        console.error('Bulk delete error:', error);
        toast.error('Failed to delete some attendees. Please try again.');
      });
    } else if (selectedAttendee) {
      // Handle single deletion
      deleteMutation.mutate(selectedAttendee.id);
    }
  };

  const handleCheckIn = (attendee) => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }
    checkInMutation.mutate(attendee.id);
  };

  const toggleAttendee = (attendeeId) => {
    setSelectedAttendees(prev =>
      prev.includes(attendeeId)
        ? prev.filter(id => id !== attendeeId)
        : [...prev, attendeeId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedAttendees.length === attendees.length && attendees.length > 0) {
      // Unselect all attendees on current page
      setSelectedAttendees(prev => prev.filter(id => !attendees.some(a => a.id === id)));
    } else {
      // Select all attendees on current page
      const currentPageIds = attendees.map(a => a.id);
      setSelectedAttendees(prev => [...new Set([...prev, ...currentPageIds])]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedAttendees.length === 0) {
      toast.warning('No attendees selected for deletion.');
      return;
    }

    const selectedAttendeesData = allAttendees.filter(a => selectedAttendees.includes(a.id));
    setSelectedAttendee({
      bulkDelete: true,
      attendees: selectedAttendeesData,
      count: selectedAttendees.length
    });
    setShowDeleteModal(true);
  };

  const handleBulkCheckIn = () => {
    if (selectedAttendees.length === 0) {
      toast.warning('No attendees selected for check-in.');
      return;
    }
    setShowCheckInModal(true);
  };

  const confirmBulkCheckIn = () => {
    bulkCheckInMutation.mutate(selectedAttendees);
    setShowCheckInModal(false);
  };

  // Navigate to analytics report
  const handleAnalyticsClick = () => {
    navigate('/analytics');
  };

  // Helper to get event identifier (slug or id)
  const getEventIdentifier = (eventId) => {
    const event = eventsData?.events?.find(e => e.id === eventId);
    return event?.slug || eventId;
  };

  // Copy registration link handler (same as Events page)
  const handleCopyRegistrationLink = async (eventId) => {
    if (!eventId) {
      toast.error('Please select an event first');
      return;
    }

    try {
      const identifier = getEventIdentifier(eventId);
      // Generate the long URL for comparison
      const currentDomain = window.location.origin;
      const longUrl = `${currentDomain}/self-register?event=${identifier}`;
      console.log('🔗 Long URL:', longUrl);

      // First try to get existing short URLs for this event
      const shortUrlsResponse = await shortUrlAPI.getAll(eventId);
      const shortUrls = shortUrlsResponse.data;
      console.log('📋 Existing short URLs:', shortUrls);

      let urlToCopy;

      if (shortUrls && shortUrls.length > 0) {
        // Use the first active short URL
        const activeShortUrl = shortUrls.find(url => url.is_active && !url.is_expired);
        if (activeShortUrl) {
          urlToCopy = activeShortUrl.short_url;
          console.log('✅ Using existing short URL:', urlToCopy);
        }
      }

      // If no short URL exists, create one
      if (!urlToCopy) {
        console.log('🔄 Creating new short URL...');
        const createResponse = await createShortUrlMutation.mutateAsync({
          eventId: eventId,
          data: {} // No expiration date
        });
        urlToCopy = createResponse.data.short_url;
        console.log('🆕 Created short URL:', urlToCopy);
      }

      // Fallback to regular registration URL if short URL fails
      if (!urlToCopy) {
        urlToCopy = longUrl;
        console.log('⚠️ Using fallback long URL:', urlToCopy);
      }

      console.log('📋 Final URL to copy:', urlToCopy);
      await navigator.clipboard.writeText(urlToCopy);
      toast.success('Registration link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy link:', error);

      // Fallback to regular registration URL
      try {
        const identifier = getEventIdentifier(eventId);
        const currentDomain = window.location.origin;
        const fallbackUrl = `${currentDomain}/self-register?event=${identifier}`;
        await navigator.clipboard.writeText(fallbackUrl);
        toast.success('Registration link copied to clipboard!');
      } catch (fallbackError) {
        toast.error('Failed to copy link. Please try again.');
      }
    }
  };

  // Open registration link in new tab
  const handleOpenRegistrationLink = (eventId) => {
    if (!eventId) {
      toast.error('Please select an event first');
      return;
    }
    const identifier = getEventIdentifier(eventId);
    const currentDomain = window.location.origin;
    const registrationUrl = `${currentDomain}/self-register?event=${identifier}`;
    window.open(registrationUrl, '_blank');
  };

  // Copy usher link
  const handleCopyUsherLink = async (eventId) => {
    if (!eventId) {
      toast.error('Please select an event first');
      return;
    }
    try {
      const identifier = getEventIdentifier(eventId);
      const currentDomain = window.location.origin;
      const usherUrl = `${currentDomain}/self-register?event=${identifier}&usher`;
      await navigator.clipboard.writeText(usherUrl);
      toast.success('Usher link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link. Please try again.');
    }
  };

  // Open usher link
  const handleOpenUsherLink = (eventId) => {
    if (!eventId) {
      toast.error('Please select an event first');
      return;
    }
    const identifier = getEventIdentifier(eventId);
    const currentDomain = window.location.origin;
    const usherUrl = `${currentDomain}/self-register?event=${identifier}&usher`;
    window.open(usherUrl, '_blank');
  };

  // Helper function to get attendee gender
  const getAttendeeGender = (attendee) => {
    // Check direct gender field
    if (attendee.gender) return attendee.gender;

    // Check in data array for gender field
    const genderField = attendee.data?.find(d =>
      d.field?.field_name === 'gender' ||
      d.field?.field_name === 'Gender'
    );

    return genderField?.value || genderField?.field_value || '';
  };

  // Use backend pagination and filtering
  const attendees = attendeesData?.attendees?.data || attendeesData?.attendees || [];
  const allAttendees = attendees; // For backward compatibility with other uses
  const filteredAttendees = attendees;

  // Pagination logic
  const totalItems = attendeesData?.attendees?.total || attendees.length;
  const totalPages = attendeesData?.attendees?.last_page || Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = attendeesData?.attendees?.from ? attendeesData.attendees.from - 1 : (currentPage - 1) * itemsPerPage;
  const endIndex = attendeesData?.attendees?.to || startIndex + attendees.length;

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, genderFilter, selectedEvent]);

  // Check URL params for gender filter and event selection on mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gender = params.get('gender');
    const event = params.get('event');

    if (gender) {
      setGenderFilter(gender);
    }

    if (event) {
      setSelectedEvent(event);
    }

    // Clear the URL params after setting the filters
    if (gender || event) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [setSelectedEvent]);

  if (isLoading) {
    return (
      <Layout>
        <Loading fullScreen text="Loading attendees..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Attendees</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage event attendees and registrations</p>
            {/* Backend Status Indicator */}
            <div className="mt-2 text-xs sm:text-sm">
              {healthCheck ? (
                <span className="text-green-600">All systems functional</span>
              ) : (
                <span className="text-red-600">Issue detected</span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            {/* Import/Export Component */}
            <AttendeeImportExport
              selectedEvent={selectedEvent}
              attendees={filteredAttendees}
              onImportComplete={() => {
                queryClient.invalidateQueries({ queryKey: ['attendees', selectedEvent] });
                queryClient.invalidateQueries({ queryKey: ['attendees'] });
                queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
              }}
            />

            {selectedEvent && canManageEmailSettings && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setShowEmailModal(true)}
                  className="gap-2 w-full sm:w-auto"
                >
                  <Mail className="h-5 w-5" />
                  <span className="hidden xs:inline">Email Settings</span>
                  <span className="xs:hidden">Email</span>
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setShowEmailComposer(true)}
                  className="gap-2 w-full sm:w-auto"
                  disabled={attendees.length === 0}
                >
                  <Send className="h-5 w-5" />
                  <span className="hidden xs:inline">Send Email</span>
                  <span className="xs:hidden">Send</span>
                </Button>
              </>
            )}
            <Button
              variant="secondary"
              onClick={handleAnalyticsClick}
              className="gap-2 w-full sm:w-auto"
              disabled={attendees.length === 0}
            >
              <FileText className="h-5 w-5" />
              <span className="hidden xs:inline">Analytics Report</span>
              <span className="xs:hidden">Analytics</span>
            </Button>
            {canDelete && selectedAttendees.length > 0 && (
              <>
                <Button
                  variant="success"
                  onClick={handleBulkCheckIn}
                  className="gap-2 w-full sm:w-auto"
                  disabled={bulkCheckInMutation.isPending}
                >
                  <UserCheck className="h-5 w-5" />
                  <span className="hidden xs:inline">Check In Selected ({selectedAttendees.length})</span>
                  <span className="xs:hidden">Check In ({selectedAttendees.length})</span>
                </Button>
                <Button
                  variant="danger"
                  onClick={handleBulkDelete}
                  className="gap-2 w-full sm:w-auto"
                >
                  <Trash2 className="h-5 w-5" />
                  <span className="hidden xs:inline">Delete Selected ({selectedAttendees.length})</span>
                  <span className="xs:hidden">Delete ({selectedAttendees.length})</span>
                </Button>
              </>
            )}
            <Button
              onClick={handleAddClick}
              className="gap-2 w-full sm:w-auto"
            >
              <Plus className="h-5 w-5" />
              <span className="hidden xs:inline">Register Attendee</span>
              <span className="xs:hidden">Register</span>
            </Button>
          </div>
        </div>

        {/* Event Selector, Gender Filter, and Search in Same Row */}
        <Card>
          <Card.Content>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 min-w-0">
                <EventSelector
                  selectedEvent={selectedEvent}
                  onEventChange={setSelectedEvent}
                  className="w-full"
                  attendeeCount={filteredAttendees.length}
                  eventsWithCounts={eventsData?.events || []}
                />
              </div>
              <div className="w-full lg:w-48 flex-shrink-0">
                <select
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm bg-white"
                >
                  <option value="">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div className="flex-1 relative min-w-0">
                <input
                  type="text"
                  placeholder="Search by name, email, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
                {searchTerm && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500 bg-white px-1">
                    {filteredAttendees.length}
                  </div>
                )}
              </div>
            </div>

            {/* Registration Link Buttons - Show only if event is selected */}
            {selectedEvent && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyRegistrationLink(selectedEvent)}
                  className="gap-2 font-medium text-blue-600 border-blue-200 hover:bg-blue-50 justify-center"
                >
                  <Copy className="h-4 w-4" />
                  Copy Registration Link
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenRegistrationLink(selectedEvent)}
                  className="gap-2 font-medium text-green-600 border-green-200 hover:bg-green-50 justify-center"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Registration Page
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyUsherLink(selectedEvent)}
                  className="gap-2 font-medium text-purple-600 border-purple-200 hover:bg-purple-50 justify-center"
                >
                  <Copy className="h-4 w-4" />
                  Copy Usher Link
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenUsherLink(selectedEvent)}
                  className="gap-2 font-medium text-teal-600 border-teal-200 hover:bg-teal-50 justify-center"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Usher Page
                </Button>
              </div>
            )}

            {/* Active Filters Display */}
            {genderFilter && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-gray-600">Active filter:</span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                  Gender: {genderFilter}
                  <button
                    onClick={() => setGenderFilter('')}
                    className="ml-1 hover:text-teal-900"
                  >
                    ×
                  </button>
                </span>
              </div>
            )}
          </Card.Content>
        </Card>

        {/* Status Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setStatusFilter('')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${statusFilter === ''
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              All
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {allAttendees.length}
              </span>
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${statusFilter === 'pending'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Pending
              <span className="ml-2 bg-yellow-100 text-yellow-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {allAttendees.filter(a => a.check_in_status === 'pending').length}
              </span>
            </button>
            <button
              onClick={() => setStatusFilter('checked_in')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${statusFilter === 'checked_in'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Checked In
              <span className="ml-2 bg-green-100 text-green-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {allAttendees.filter(a => a.check_in_status === 'checked_in').length}
              </span>
            </button>
          </nav>
        </div>

        {/* Attendees Table */}
        <Card>
          <Card.Content>
            {/* Select All */}
            {canDelete && attendees.length > 0 && (
              <div className="flex items-center p-2 border-b mb-3">
                <input
                  type="checkbox"
                  checked={selectedAttendees.length === attendees.length && attendees.length > 0}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <label className="ml-3 text-sm font-medium text-gray-700">Select All</label>
                {selectedAttendees.length > 0 && (
                  <span className="ml-2 text-xs text-gray-500">
                    ({selectedAttendees.length} selected)
                  </span>
                )}
              </div>
            )}

            {/* Mobile Card View */}
            <div className="block sm:hidden space-y-3">
              {attendees.map((attendee) => (
                <div key={attendee.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {canDelete && (
                        <input
                          type="checkbox"
                          checked={selectedAttendees.includes(attendee.id)}
                          onChange={() => toggleAttendee(attendee.id)}
                          className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 mt-0.5 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {getAttendeeName(attendee)}
                        </h3>
                        <p className="text-xs text-gray-500 truncate">{attendee.event?.name || 'N/A'}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 ${attendee.check_in_status === 'checked_in' ? 'bg-green-100 text-green-800' :
                      attendee.check_in_status === 'checked_out' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                      {CHECK_IN_STATUS_LABELS[attendee.check_in_status] || attendee.check_in_status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      {new Date(attendee.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-2">
                      {attendee.check_in_status !== 'checked_in' && (
                        <button
                          onClick={() => handleCheckIn(attendee)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title="Check In"
                        >
                          <UserCheck className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEditClick(attendee)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => {
                            setSelectedAttendee(attendee);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {canDelete && (
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedAttendees.length === attendees.length && attendees.length > 0}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                      </th>
                    )}
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attendee
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Event
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                      Registered
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendees.map((attendee) => (
                    <tr key={attendee.id} className="hover:bg-gray-50">
                      {canDelete && (
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedAttendees.includes(attendee.id)}
                            onChange={() => toggleAttendee(attendee.id)}
                            className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                          />
                        </td>
                      )}
                      <td className="px-4 lg:px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-32 lg:max-w-none">
                          {getAttendeeName(attendee)}
                        </div>
                      </td>
                      <td className="px-4 lg:px-6 py-4 hidden md:table-cell">
                        <div className="text-sm text-gray-900 truncate max-w-32">{attendee.event?.name || 'N/A'}</div>
                      </td>
                      <td className="px-4 lg:px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${attendee.check_in_status === 'checked_in' ? 'bg-green-100 text-green-800' :
                          attendee.check_in_status === 'checked_out' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                          {CHECK_IN_STATUS_LABELS[attendee.check_in_status] || attendee.check_in_status}
                        </span>
                      </td>
                      <td className="px-4 lg:px-6 py-4 text-sm text-gray-500 hidden lg:table-cell">
                        {new Date(attendee.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 lg:px-6 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1">
                          {attendee.check_in_status !== 'checked_in' && (
                            <button
                              onClick={() => handleCheckIn(attendee)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded"
                              title="Check In"
                            >
                              <UserCheck className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleEditClick(attendee)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          {canDelete && (
                            <button
                              onClick={() => {
                                setSelectedAttendee(attendee);
                                setShowDeleteModal(true);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {attendees.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No attendees found</p>
              </div>
            )}
          </Card.Content>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 py-3 border border-gray-200 rounded-lg">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{' '}
              <span className="font-medium">{totalItems}</span> results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                  // Show first page, last page, current page, and pages around current
                  const showPage = page === 1 || page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1);

                  if (!showPage) {
                    // Show ellipsis
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-2 text-gray-400">...</span>;
                    }
                    return null;
                  }

                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === page
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Unified Attendee Modal */}
        <AttendeeModal
          isOpen={showAttendeeModal}
          onClose={() => {
            setShowAttendeeModal(false);
            setSelectedAttendee(null);
            setModalMode('add');
          }}
          onSubmit={handleAttendeeSubmit}
          attendee={modalMode === 'edit' ? selectedAttendee : null}
          loading={attendeeMutation.isPending}
          selectedEvent={selectedEvent ? eventsData?.events?.find(e => e.id === selectedEvent) : null}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedAttendee(null);
          }}
          onConfirm={handleDelete}
          title={selectedAttendee?.bulkDelete ? "Delete Multiple Attendees" : "Delete Attendee"}
          message={
            selectedAttendee?.bulkDelete
              ? `Are you sure you want to delete ${selectedAttendee.count} attendees? This action cannot be undone.`
              : "Are you sure you want to delete this attendee? This action cannot be undone."
          }
          confirmText={selectedAttendee?.bulkDelete ? `Delete ${selectedAttendee.count} Attendees` : "Delete"}
          cancelText="Cancel"
          variant="danger"
          loading={deleteMutation.isPending}
        />

        {/* Bulk Check-In Confirmation Modal */}
        <ConfirmModal
          isOpen={showCheckInModal}
          onClose={() => setShowCheckInModal(false)}
          onConfirm={confirmBulkCheckIn}
          title="Check In Attendees"
          message={`Are you sure you want to check in ${selectedAttendees.length} selected attendee${selectedAttendees.length > 1 ? 's' : ''}? This will mark them as present.`}
          confirmText={`Check In ${selectedAttendees.length} Attendee${selectedAttendees.length > 1 ? 's' : ''}`}
          cancelText="Cancel"
          variant="success"
          loading={bulkCheckInMutation.isPending}
        />

        {/* Email Notification Settings Modal */}
        <EmailNotificationModal
          isOpen={showEmailModal && !!selectedEvent}
          onClose={() => setShowEmailModal(false)}
          event={selectedEvent ? eventsData?.events?.find(e => e.id === selectedEvent) : null}
          onUpdate={() => {
            // Refresh all relevant caches to get updated email settings
            queryClient.invalidateQueries({ queryKey: ['events'] });
            queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
            queryClient.invalidateQueries({ queryKey: ['event-details', selectedEvent] }); // Fresh event data for AttendeeModal
            queryClient.invalidateQueries({ queryKey: ['registration-config', selectedEvent] });
            queryClient.invalidateQueries({ queryKey: ['fields', selectedEvent] });
            // Also invalidate any token-based registration configs
            queryClient.invalidateQueries({ queryKey: ['registration-config'] });
          }}
        />

        {/* Email Composer Modal */}
        <Modal
          isOpen={showEmailComposer}
          onClose={() => setShowEmailComposer(false)}
          title="Compose Email"
          size="xl"
        >
          <EmailComposer
            eventId={selectedEvent}
            attendees={attendees}
            initialData={selectedAttendees.length > 0 ? { individual_recipients: selectedAttendees } : null}
            onSendComplete={() => {
              setShowEmailComposer(false);
              // Optionally refresh data after sending
              queryClient.invalidateQueries({ queryKey: ['attendees', selectedEvent] });
              queryClient.invalidateQueries({ queryKey: ['attendees'] });
              setSelectedAttendees([]); // Clear selection after sending
            }}
          />
        </Modal>

      </div>
    </Layout>
  );
};

export default Attendees;
